import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import AnimateHeight from 'react-animate-height';

import { MotifIconButton } from '../button/icon-button/icon-button';
import { ReactComponent as ActionIcSearch24px } from '../../assets/icons/action/ic_search_24px.svg';
import { ReactComponent as NavigationIcClose24px } from '../../assets/icons/navigation/ic_close_24px.svg';

const defaultIcons = {
  close: <NavigationIcClose24px />,
  search: <ActionIcSearch24px />
};

/**
 * @typedef Props
 * @prop {boolean} [fixed]
 * @prop {React.ReactNode} [logo]
 * @prop {React.ReactNode} [searchInput]
 * @prop {React.ReactNode} [iconButton]
 * @prop {{close: React.ReactNode, search: React.ReactNode}} [icons]
 * @prop {React.ReactNode} [leftComponents]
 * @prop {React.ReactNode} [iconsContainer]
 * @prop {React.ReactNode} [menu]
 * @prop {{}} [menuRef]
 * @prop {string} [appHeaderName]
 * @prop {{}} [searchOpenCloseButtonProps]
 * @prop {string} [className]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifHeader = (props) => {
  const {
    fixed = false,
    logo = null,
    searchInput = null,
    iconButton = null,
    icons: userIcons = {},
    leftComponents = null,
    iconsContainer = null,
    menu = null,
    menuRef = null,
    appHeaderName = '',
    searchOpenCloseButtonProps = {},
    className = '',
    ...other
  } = props;
  const icons = {
    ...defaultIcons,
    ...userIcons
  };
  const [searchOpened, setSearchOpened] = useState(false);
  const [mobile, setMobile] = useState(false);

  const headerRef = useRef(null);
  const node = useRef(null);
  const paddingTopBody = useRef(null);

  useEffect(() => {
    if (fixed) {
      paddingTopBody.current = document.body.style.paddingTop;
      document.body.style.paddingTop = `${headerRef.current.clientHeight}px`;
    }

    const windowResize = () => {
      const currentMobile = window.innerWidth < 768;
      if (currentMobile !== mobile) {
        setMobile(currentMobile);
      }
    };

    window.addEventListener('resize', windowResize);

    return () => {
      window.removeEventListener('resize', windowResize);
      if (fixed) {
        document.body.style.paddingTop = paddingTopBody.current;
      }
    };
  }, []);

  const handleClickSearchMenu = () => {
    setSearchOpened(!searchOpened);
  };

  useLayoutEffect(() => {
    if (searchOpened) {
      node.current.querySelectorAll('.motif-input')[0].focus();
    }
  }, [searchOpened]);

  const hasSearch = searchInput !== null;

  const classes = classNames({
    'motif-header': true,
    'motif-header-fixed': fixed,
    'motif-header-search-open': searchOpened,
    'motif-header-menu-open': menu && menu.props.show,
    'motif-header-has-search': hasSearch,
    [className]: true
  });

  const searchContentClasses = classNames('motif-end-xs', 'motif-header-search');

  const searchContentWrapperClasses = classNames(
    'motif-row',
    'motif-middle-xs',
    'motif-between-xs',
    'motif-header-search-content-wrapper'
  );

  return (
    <header {...other} className={classes} ref={headerRef} data-motif-header>
      <div className="motif-row motif-middle-xs motif-between-xs">
        {leftComponents}
        <div className="motif-header-company-container motif-col-lg">
          {logo}
          <span className="motif-header-app-name">{appHeaderName}</span>
        </div>
        <div>
          <div ref={node} className={searchContentWrapperClasses}>
            {hasSearch && (
              <div className={searchContentClasses}>
                <MotifIconButton
                  className="motif-header-search-button"
                  aria-label="Open Search"
                  onClick={handleClickSearchMenu}
                  {...searchOpenCloseButtonProps}
                >
                  <span className="motif-header-search-icon">{icons.search}</span>
                  <span className="motif-header-close-icon">{icons.close}</span>
                </MotifIconButton>
                {mobile ? (
                  <AnimateHeight
                    duration={150}
                    height={searchOpened ? 'auto' : 0}
                    className="motif-header-search-content-mobile"
                  >
                    <div className="motif-header-search-field">{searchInput}</div>
                  </AnimateHeight>
                ) : (
                  <div className="motif-header-search-input">
                    <div className="motif-header-search-field">{searchInput}</div>
                  </div>
                )}
              </div>
            )}
            {iconsContainer && (
              <div className="motif-header-icons-placeholder">{iconsContainer}</div>
            )}
            <div ref={menuRef} className="motif-header-menu-container motif-end-xs">
              {iconButton && <div className="motif-header-menu-open-button">{iconButton}</div>}
              {menu}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

MotifHeader.propTypes = {
  fixed: PropTypes.bool,
  iconButton: PropTypes.node,
  icons: PropTypes.shape({
    close: PropTypes.node,
    search: PropTypes.node
  }),
  // Renders a component to the left side of logo
  leftComponents: PropTypes.node,
  iconsContainer: PropTypes.node,
  appHeaderName: PropTypes.string,
  logo: PropTypes.node,
  menu: PropTypes.node,
  menuRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  searchInput: PropTypes.node,
  searchOpenCloseButtonProps: PropTypes.instanceOf(Object),
  className: PropTypes.string
};

export { MotifHeader };
